import { isRecruiterChannelEnabled } from '@kilat/shared';

import { LocaleLink as Link } from '../lib/locale-link.js';
import { useMarketingSeo } from '../lib/use-marketing-seo.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useI18n } from '../lib/use-i18n.js';

/**
 * /become-a-recruiter — Sales landing for the recruiter persona
 * (Sprint 82, recruiter epic Sprint 3 of ~4).
 *
 * Recruiters sign up sales agents who then sign up operators. They earn
 * a configurable share of every downstream operator's invoice for the
 * lifetime of the relationship. Unlike `/become-an-agent` which is
 * instant-activation (ADR-0015), recruiter applications go through
 * super-admin moderation — bigger commission stakes → more scrutiny.
 *
 * The actual application form lives at `/become-a-recruiter/signup`.
 */
export function BecomeARecruiterPage() {
  const brand = usePlatformBrand();
  const { t } = useI18n();
  useMarketingSeo({
    title: t('marketing.recruiter.metaTitle', { brand: brand.name }),
    description: t('marketing.recruiter.metaDescription', { brand: brand.name }),
  });
  // ADR-0023: when the brand's recruiter channel is disabled, don't show the
  // sales pitch — render a clear "not open right now" screen with a way back.
  if (!isRecruiterChannelEnabled(brand)) {
    return <RecruiterUnavailable />;
  }
  return (
    <main
      style={{
        minHeight: '100dvh',
        position: 'relative',
        background: '#fafaf7',
        color: '#0f172a',
        overflow: 'hidden',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
      }}
    >
      {/* Ambient gradients — recruiter-flavoured (amber leading like
          the agent signup, but with a denser top-right halo to feel
          more authoritative). */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(80% 60% at 90% -10%, rgba(245,158,11,0.20), transparent 60%),' +
            'radial-gradient(60% 50% at -10% 10%, rgba(13,122,79,0.12), transparent 60%),' +
            'radial-gradient(70% 50% at 50% 110%, rgba(13,122,79,0.08), transparent 60%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(var(--tint-subtle) 1px, transparent 1px),' +
            'linear-gradient(90deg, var(--tint-subtle) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          maskImage: 'radial-gradient(70% 70% at 50% 30%, #000 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(70% 70% at 50% 30%, #000 30%, transparent 80%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 880,
          margin: '0 auto',
          padding: '20px 20px 64px',
        }}
      >
        {/* Top bar */}
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: 4,
            paddingBottom: 16,
          }}
        >
          <Link to="/" style={pillNav}>
            <span aria-hidden style={pillArrow}>
              ←
            </span>
            <span>{brand.shortName}</span>
          </Link>
          <Link
            to="/become-an-agent"
            style={{ color: '#475569', textDecoration: 'none', fontSize: 13, fontWeight: 500 }}
          >
            {t('marketing.recruiter.altCta')}
          </Link>
        </header>

        {/* Hero */}
        <section style={{ marginTop: 40, display: 'grid', gap: 18 }}>
          <span style={eyebrowChip}>
            <span aria-hidden style={eyebrowDot} />
            {t('marketing.recruiter.landingEyebrow')}
          </span>
          <h1
            style={{
              margin: 0,
              fontSize: 44,
              lineHeight: 1.05,
              fontWeight: 700,
              letterSpacing: -0.5,
            }}
          >
            {t('marketing.recruiter.headlinePrefix')}{' '}
            <span style={gradientText}>{t('marketing.recruiter.headlineGradient2')}</span>
          </h1>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.55, color: '#475569', maxWidth: 600 }}>
            {t('marketing.recruiter.subLong', { brand: brand.name })}
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
            <Link to="/become-a-recruiter/signup" style={primaryCta}>
              {t('marketing.recruiter.applyCta')}{' '}
              <span aria-hidden style={{ marginLeft: 6 }}>
                →
              </span>
            </Link>
            <a href="#how-it-works" style={ghostCta}>
              {t('marketing.recruiter.howCta')}
            </a>
          </div>
          <p
            style={{
              margin: '4px 0 0 0',
              fontSize: 12,
              color: '#64748b',
              maxWidth: 600,
            }}
          >
            {t('marketing.recruiter.applicationFootnote')}
          </p>
        </section>

        {/* Three pillars */}
        <section id="how-it-works" style={{ marginTop: 48, display: 'grid', gap: 16 }}>
          <h2 style={sectionTitle}>{t('marketing.recruiter.howHeading')}</h2>
          <div style={pillarGrid}>
            <Pillar
              num="1"
              title={t('marketing.recruiter.step1Title')}
              body={t('marketing.recruiter.step1Body')}
            />
            <Pillar
              num="2"
              title={t('marketing.recruiter.step2Title')}
              body={t('marketing.recruiter.step2Body')}
            />
            <Pillar
              num="3"
              title={t('marketing.recruiter.step3Title')}
              body={t('marketing.recruiter.step3Body')}
            />
          </div>
        </section>

        {/* Money + payout */}
        <section style={{ marginTop: 48, display: 'grid', gap: 16 }}>
          <h2 style={sectionTitle}>{t('marketing.recruiter.moneyHeading')}</h2>
          <div style={moneyCard}>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: '#334155' }}>
              <strong>{t('marketing.recruiter.moneyIntroLead')}</strong>
              {t('marketing.recruiter.moneyIntro')}
            </p>
            <ul style={moneyList}>
              <li>
                <strong>{t('marketing.recruiter.moneyBullet1Lead')}</strong>
                {t('marketing.recruiter.moneyBullet1')}
              </li>
              <li>
                <strong>{t('marketing.recruiter.moneyBullet2Lead')}</strong>
                {t('marketing.recruiter.moneyBullet2')}
              </li>
              <li>
                <strong>{t('marketing.recruiter.moneyBullet3Lead')}</strong>
                {t('marketing.recruiter.moneyBullet3')}
              </li>
              <li>
                <strong>{t('marketing.recruiter.moneyBullet4Lead')}</strong>
                {t('marketing.recruiter.moneyBullet4')}
              </li>
            </ul>
          </div>
        </section>

        {/* Bottom CTA */}
        <section
          style={{
            marginTop: 56,
            padding: '28px 24px',
            borderRadius: 24,
            background:
              'linear-gradient(135deg, rgba(13,122,79,0.10) 0%, rgba(245,158,11,0.12) 100%)',
            border: '1px solid rgba(13,122,79,0.18)',
            textAlign: 'center',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
            {t('marketing.recruiter.bottomHeading')}
          </h2>
          <p
            style={{
              margin: '8px auto 0',
              fontSize: 14,
              lineHeight: 1.55,
              color: '#475569',
              maxWidth: 480,
            }}
          >
            {t('marketing.recruiter.bottomBody')}
          </p>
          <Link to="/become-a-recruiter/signup" style={{ ...primaryCta, marginTop: 20 }}>
            {t('marketing.recruiter.bottomCta')}{' '}
            <span aria-hidden style={{ marginLeft: 6 }}>
              →
            </span>
          </Link>
        </section>
      </div>
    </main>
  );
}

/**
 * ADR-0023 "recruiter channel is off" state, shared by `/become-a-recruiter`
 * and its `/signup` form so a direct link / bookmark never dead-ends on a
 * disabled programme. No-dead-ends: says what state it's in + gives the next
 * action (become an agent, or back home).
 */
export function RecruiterUnavailable() {
  const brand = usePlatformBrand();
  const { t } = useI18n();
  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background: '#fafaf7',
        color: '#0f172a',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
      }}
    >
      <div style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
        <span style={{ ...eyebrowChip, margin: '0 auto' }}>
          <span aria-hidden style={eyebrowDot} />
          {t('marketing.recruiter.unavailableEyebrow')}
        </span>
        <h1
          style={{
            margin: '16px 0 0 0',
            fontSize: 34,
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: -0.5,
          }}
        >
          {t('marketing.recruiter.unavailableTitle')}
        </h1>
        <p style={{ margin: '14px 0 0 0', fontSize: 15, color: '#475569', lineHeight: 1.55 }}>
          {t('marketing.recruiter.unavailableBody', { brand: brand.name })}
        </p>
        <div
          style={{
            display: 'flex',
            gap: 12,
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginTop: 28,
          }}
        >
          <Link to="/become-an-agent" style={primaryCta}>
            {t('marketing.recruiter.unavailableBecomeAgent')}{' '}
            <span aria-hidden style={{ marginLeft: 6 }}>
              →
            </span>
          </Link>
          <Link to="/" style={ghostCta}>
            {t('marketing.recruiter.unavailableBack', { brand: brand.shortName })}
          </Link>
        </div>
      </div>
    </main>
  );
}

function Pillar({
  num,
  title,
  body,
}: {
  readonly num: string;
  readonly title: string;
  readonly body: string;
}) {
  return (
    <article style={pillarCard}>
      <span style={pillarNum}>{num}</span>
      <h3 style={{ margin: '12px 0 6px 0', fontSize: 16, fontWeight: 600 }}>{title}</h3>
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: '#475569' }}>{body}</p>
    </article>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────

const pillNav: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px 8px 8px',
  borderRadius: 999,
  background: 'rgba(255,255,255,0.65)',
  backdropFilter: 'blur(8px)',
  border: '1px solid var(--color-border)',
  color: '#0f172a',
  textDecoration: 'none',
  fontSize: 13,
  fontWeight: 500,
  boxShadow: '0 1px 2px var(--tint-subtle)',
};
const pillArrow: React.CSSProperties = {
  width: 24,
  height: 24,
  borderRadius: 999,
  background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
  color: '#fff',
  display: 'grid',
  placeItems: 'center',
  fontSize: 14,
  lineHeight: 1,
  fontWeight: 700,
};
const eyebrowChip: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 12px',
  borderRadius: 999,
  background: 'rgba(245,158,11,0.12)',
  color: '#b45309',
  fontSize: 11,
  letterSpacing: 1.5,
  fontWeight: 600,
  textTransform: 'uppercase',
  width: 'fit-content',
};
const eyebrowDot: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: 999,
  background: '#f59e0b',
};
const gradientText: React.CSSProperties = {
  background: 'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};
const primaryCta: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '14px 22px',
  borderRadius: 14,
  background: 'linear-gradient(135deg, #0d7a4f 0%, #0f8659 100%)',
  color: '#ffffff',
  fontWeight: 600,
  fontSize: 14,
  textDecoration: 'none',
  boxShadow: '0 1px 2px rgba(13,122,79,0.4), 0 8px 20px rgba(13,122,79,0.28)',
};
const ghostCta: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '14px 18px',
  borderRadius: 14,
  background: 'rgba(255,255,255,0.6)',
  backdropFilter: 'blur(8px)',
  color: '#0f172a',
  fontWeight: 500,
  fontSize: 14,
  textDecoration: 'none',
  border: '1px solid var(--color-border)',
};
const sectionTitle: React.CSSProperties = {
  margin: 0,
  fontSize: 22,
  fontWeight: 700,
  letterSpacing: -0.3,
};
const pillarGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
  gap: 12,
};
const pillarCard: React.CSSProperties = {
  padding: '20px 18px',
  borderRadius: 16,
  background: 'rgba(255,255,255,0.7)',
  backdropFilter: 'blur(8px)',
  border: '1px solid var(--color-border)',
  boxShadow: '0 1px 2px var(--tint-subtle)',
};
const pillarNum: React.CSSProperties = {
  display: 'inline-grid',
  placeItems: 'center',
  width: 32,
  height: 32,
  borderRadius: 10,
  background: 'linear-gradient(135deg, #0d7a4f 0%, #0f8659 100%)',
  color: '#ffffff',
  fontWeight: 700,
  fontSize: 14,
};
const moneyCard: React.CSSProperties = {
  padding: '24px 22px',
  borderRadius: 18,
  background: 'var(--glass-bg)',
  backdropFilter: 'blur(12px)',
  border: '1px solid var(--color-border)',
  boxShadow: '0 1px 2px var(--tint-subtle), 0 8px 24px var(--color-border)',
  display: 'grid',
  gap: 14,
};
const moneyList: React.CSSProperties = {
  margin: 0,
  paddingLeft: 18,
  display: 'grid',
  gap: 6,
  fontSize: 13,
  lineHeight: 1.6,
  color: '#475569',
};
